import React from 'react';
import { SimpleGrid } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import Link from 'next/link';
import type { UseFormReturnType } from '@mantine/form';

import { PhoneInput, TextInput } from '~/domains/design-system/TextInput';
import { Text } from '~/domains/design-system/Text';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { createEnterKeyHandler } from '~/domains/auth/utils/formHelpers';
import { useIsCanada } from '~/domains/common/hooks/useIsCanada';
import { Routes } from '~/domains/common/constants/routes';
import { VectorFastIcon } from '~/domains/design-system/vectors/VectorFastIcon';
import { VectorProveLogo } from '~/domains/design-system/vectors/VectorProveLogo';
import { UnstyledButton } from '~/domains/design-system/Button';
import { IconChevronRight } from '~/domains/design-system/icons/IconChevronRight';
import dt from '~/testing';

import { ConnectedRegistrationBonusBanner } from '../../RegistrationBonusBanner/ConnectedRegistrationBonusBanner';
import type { KYCFormValues } from '../types';
import { useKYCFormHelpers } from '../utils/useKYCFormHelpers';

import { SignUpContinueButton } from './SignUpContinueButton';
import useStyles from './styles';

interface KYCStartFormProps {
  form: UseFormReturnType<KYCFormValues>;
  onContinue: () => void;
  onEnterManually: () => void;
  isLoading?: boolean;
  isRestrictedDomain?: boolean;
}

export function KYCStartForm({
  form,
  onContinue,
  onEnterManually,
  isLoading,
  isRestrictedDomain,
}: KYCStartFormProps) {
  const { t } = useTranslation('auth');
  const { classes } = useStyles();
  const isCanada = useIsCanada();
  const { formFieldChange } = useKYCFormHelpers(form);

  const isButtonDisabled =
    !form.isValid('phone') ||
    !form.isValid('dob.month') ||
    !form.isValid('dob.day') ||
    !form.isValid('dob.year');

  const handleKeyDown = createEnterKeyHandler(isButtonDisabled || !!isLoading, onContinue);

  if (isRestrictedDomain) {
    return null;
  }

  return (
    <>
      <SignUpHeader
        title={t('signUp.headers.kycStart.title')}
        copy={t('signUp.headers.kycStart.copy')}
        isLogoVisible={false}
      />

      <div className={classes.proveCard}>
        <div className={classes.proveCardHeader}>
          <div className={classes.kycStartBadge}>
            <VectorFastIcon height={16} />
            <Text variant="label-small" fw={600}>
              {t('signUp.headers.kycStart.badgeTitle')}
            </Text>
          </div>
          <VectorProveLogo height={16} />
        </div>

        <div className={classes.kycStartFieldGroup}>
          <Text variant="subhead-medium" fw={600} c="gray2.6">
            {t('signUp.headers.kycStart.phoneHeading')}
          </Text>
          <PhoneInput
            data-test-id={dt.auth.kycStartForm.phoneInput}
            className={classes.textInput}
            prefix="+1"
            label={t('signUp.phone.label')}
            maxLength={10}
            w="100%"
            onKeyDown={handleKeyDown}
            {...form.getInputProps('phone')}
          />
        </div>

        <div className={classes.kycStartFieldGroup}>
          <Text variant="subhead-medium" fw={600} c="gray2.6">
            {t('signUp.headers.kycStart.dobHeading')}
          </Text>
          <SimpleGrid spacing="md" cols={3}>
            <TextInput
              data-test-id={dt.auth.kycStartForm.monthField}
              className={classes.textInput}
              label={t('signUp.dob.month.label')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('dob.month', { type: 'input' })}
              onChange={(e) => formFieldChange('dob.month', e)}
            />
            <TextInput
              data-test-id={dt.auth.kycStartForm.dateField}
              className={classes.textInput}
              label={t('signUp.dob.day.label')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('dob.day')}
              onChange={(e) => formFieldChange('dob.day', e)}
            />
            <TextInput
              data-test-id={dt.auth.kycStartForm.yearField}
              className={classes.textInput}
              label={t('signUp.dob.year.label')}
              onKeyDown={handleKeyDown}
              {...form.getInputProps('dob.year')}
              onChange={(e) => formFieldChange('dob.year', e)}
            />
          </SimpleGrid>
        </div>

        <Text className={classes.legalText}>
          <Trans
            i18nKey="auth:signUp.headers.kycStart.legalText"
            components={{
              termsLink: (
                <Link href={Routes.termsOfUse(isCanada)} target="_blank">
                  {t('signUp.terms')}
                </Link>
              ),
              privacyLink: (
                <Link href={Routes.privacyPolicy(isCanada)} target="_blank">
                  {t('signUp.privacyPolicy')}
                </Link>
              ),
              learnMoreLink: (
                <Link
                  href="https://www.prove.com/legal/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('signUp.terms')}
                </Link>
              ),
            }}
          />
        </Text>

        <ConnectedRegistrationBonusBanner />

        <SignUpContinueButton
          onClick={onContinue}
          disabled={isButtonDisabled || isLoading}
          loading={isLoading}
          data-test-id={dt.auth.kycStartForm.continueButton}
        />
      </div>

      <UnstyledButton
        className={classes.enterManuallyButton}
        onMouseDown={(e: React.MouseEvent) => {
          e.preventDefault();
          form.clearErrors();
          onEnterManually();
        }}
        data-test-id={dt.auth.kycStartForm.enterManuallyLink}
      >
        <Text variant="subhead-medium" fw={600} c="gray2.4" component="span">
          or{' '}
        </Text>
        <Text variant="subhead-medium" fw={600} c="gray2.7" component="span">
          {t('signUp.headers.kycStart.enterManually')}
        </Text>
        <IconChevronRight size={16} />
      </UnstyledButton>
    </>
  );
}
